import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { HelpLink } from './DirectorHelp.tsx';
import {
  MAX_INGEST_INPUTS,
  MAX_INGEST_NAME,
  ingestStateLabel,
  ingestUrl,
  type IngestInputView,
  type IngestStatus,
  type StreamSource,
} from '@rabbitfuel/shared';
import { api } from '../lib/api.ts';

/** Ob der Block offen ist, merkt sich der Browser -- wie bei der Werbung. */
const OPEN_KEY = 'rabbitfuel:regie-eingang';
/** So oft fragt der offene Block nach, ob ein Signal ankommt. */
const POLL_MS = 3000;

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * RTMP-Eingang: Drohne, Action-Cam oder ein per App geteilter Handy-Bildschirm.
 *
 * Hier legt die Regie Eingaenge an und liest ab, was die sendende App
 * braucht: Server und Schluessel (Larix, OBS) oder die komplette Adresse
 * (DJI Fly). Geschaltet wird ein Eingang danach nicht hier, sondern in der
 * Vorschau -- dort steht er wie ein Handy, mit Gross/Klein und 🎤.
 */
export function IngestDesk({
  slug,
  pin,
  sources,
}: {
  slug: string;
  pin: string | undefined;
  /** Die Quellen im Raum -- aendert sich eine RTMP-Quelle, fragt der Block gleich nach. */
  sources: StreamSource[];
}) {
  const [open, setOpen] = useState(readOpen);
  const [status, setStatus] = useState<IngestStatus | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, next ? '1' : '0');
    } catch {
      /* Privater Modus -- dann eben nur bis zum Neuladen. */
    }
  };

  const load = useCallback(() => {
    api
      .getIngest(slug, pin)
      .then(setStatus)
      .catch(() => undefined);
  }, [slug, pin]);

  // Einmal beim Laden, dann im Takt, solange der Block offen ist.
  useEffect(() => {
    load();
    if (!open) return;
    const timer = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer);
  }, [load, open]);

  // Geht eine RTMP-Quelle live oder verschwindet, nicht auf den Takt warten.
  const ingestKey = sources
    .filter((s) => s.ingest)
    .map((s) => `${s.id}:${s.status}:${s.live}`)
    .join('|');
  useEffect(load, [ingestKey, load]);

  /** Fuehrt eine Aenderung aus und uebernimmt den neuen Stand. Gibt zurueck, ob es geklappt hat. */
  const run = async (job: () => Promise<IngestStatus>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      setStatus(await job());
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hat nicht geklappt.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    void run(() => api.addIngest(slug, pin, name.trim())).then((ok) => ok && setName(''));
  };

  const inputs = status?.inputs ?? [];
  const live = inputs.filter((i) => i.state === 'live').length;

  return (
    <section className={`deck${open ? '' : ' is-collapsed'}`}>
      <header className="deck__head">
        <button type="button" className="deck__toggle" onClick={toggle} aria-expanded={open}>
          {open ? '▾' : '▸'} RTMP-Eingang
        </button>
        <HelpLink topic="rtmp-eingang" />
        <span className={`deck__mode${live ? '' : ' deck__mode--off'}`}>
          {inputs.length ? `${live} von ${inputs.length} empfängt` : 'keiner'}
        </span>
      </header>

      {open && (
        <div className="deck__body">
          <div className="deck__block">
            {status && !status.enabled ? (
              <div className="notice notice--stage">{status.message ?? 'Der RTMP-Eingang ist hier nicht verfügbar.'}</div>
            ) : (
              <>
                {status?.message && <div className="notice notice--stage">{status.message}</div>}
                {inputs.length === 0 ? (
                  <div className="deck__hint">
                    Noch kein Eingang. Lege unten einen an — zum Beispiel „Drohne“ — und trag Adresse und Schlüssel in
                    der sendenden App ein.
                  </div>
                ) : (
                  <ul className="ingest-list">
                    {inputs.map((input) => (
                      <IngestRow
                        key={input.id}
                        input={input}
                        server={status?.server ?? ''}
                        busy={busy}
                        onRename={(next) => void run(() => api.updateIngest(slug, pin, input.id, { name: next }))}
                        onRenew={() => {
                          if (
                            window.confirm(
                              `Neuen Schlüssel für „${input.name}“ erzeugen? Wer mit dem alten sendet, wird sofort getrennt und muss den neuen eintragen.`,
                            )
                          ) {
                            void run(() => api.updateIngest(slug, pin, input.id, { renewKey: true }));
                          }
                        }}
                        onRemove={() => {
                          if (window.confirm(`Eingang „${input.name}“ entfernen? Ein laufendes Signal wird getrennt.`)) {
                            void run(() => api.removeIngest(slug, pin, input.id));
                          }
                        }}
                      />
                    ))}
                  </ul>
                )}

                <form className="form-inline" onSubmit={add}>
                  <input
                    className="deck__input"
                    value={name}
                    maxLength={MAX_INGEST_NAME}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Neuer Eingang, z. B. Drohne"
                    aria-label="Name des neuen Eingangs"
                    disabled={inputs.length >= MAX_INGEST_INPUTS}
                  />
                  <button
                    className="btn btn--sm"
                    type="submit"
                    disabled={busy || !name.trim() || inputs.length >= MAX_INGEST_INPUTS}
                  >
                    Anlegen
                  </button>
                </form>
                {error && <div className="notice notice--stage">{error}</div>}
                <div className="deck__hint">
                  <b>DJI Fly:</b> unter Livestream → RTMP die komplette Adresse eintragen. <b>Larix, OBS, Prism:</b>{' '}
                  Server und Schlüssel getrennt. <b>iPhone-Bildschirm:</b> mit einer App wie Larix Screencaster an die
                  komplette Adresse senden. Sobald das Signal steht, erscheint es oben in der Vorschau — groß, klein und
                  🎤 wie bei einem Handy. Höchstens {MAX_INGEST_INPUTS} Eingänge; RTMP ist unverschlüsselt, den Schlüssel
                  also nicht weitergeben.
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function IngestRow({
  input,
  server,
  busy,
  onRename,
  onRenew,
  onRemove,
}: {
  input: IngestInputView;
  server: string;
  busy: boolean;
  onRename: (name: string) => void;
  onRenew: () => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState(input.name);
  const [showKey, setShowKey] = useState(false);
  useEffect(() => setDraft(input.name), [input.name]);

  const commit = () => {
    const next = draft.replace(/\s+/g, ' ').trim();
    if (!next) return setDraft(input.name);
    if (next !== input.name) onRename(next);
  };

  const detail =
    input.state !== 'idle' && input.video
      ? ` · ${input.video.width}×${input.video.height}${input.audio ? ' · mit Ton' : ' · ohne Ton'}`
      : '';

  return (
    <li className={`ingest-item ingest-item--${input.state}`}>
      <div className="ingest-item__head">
        <input
          className="ingest-item__name"
          value={draft}
          maxLength={MAX_INGEST_NAME}
          aria-label="Name des Eingangs"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        <span className={`ingest-state ingest-state--${input.state}`}>
          {ingestStateLabel[input.state]}
          {detail}
        </span>
      </div>
      <CopyField label="Server" value={server} />
      <CopyField label="Schlüssel" value={input.key} hidden={!showKey} onReveal={() => setShowKey((v) => !v)} />
      <CopyField label="Komplett" value={ingestUrl(server, input.key)} hidden={!showKey} />
      <div className="ingest-item__actions">
        <button type="button" className="deck__link" onClick={onRenew} disabled={busy}>
          Neuer Schlüssel
        </button>
        <button type="button" className="deck__link deck__link--danger" onClick={onRemove} disabled={busy}>
          Entfernen
        </button>
      </div>
    </li>
  );
}

/** Ein Wert zum Abschreiben -- mit Knopf zum Kopieren, Geheimes erst auf Wunsch sichtbar. */
function CopyField({
  label,
  value,
  hidden = false,
  onReveal,
}: {
  label: string;
  value: string;
  hidden?: boolean;
  onReveal?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Zum Kopieren markieren:', value);
    }
  };
  return (
    <div className="ingest-field">
      <span className="ingest-field__label">{label}</span>
      <code className="ingest-field__value">{hidden ? '•'.repeat(Math.min(24, value.length)) : value}</code>
      <div className="ingest-field__buttons">
        {onReveal && (
          <button type="button" className="chip" onClick={onReveal}>
            {hidden ? 'Zeigen' : 'Verbergen'}
          </button>
        )}
        <button type="button" className="chip" onClick={() => void copy()}>
          {copied ? 'Kopiert ✓' : 'Kopieren'}
        </button>
      </div>
    </div>
  );
}
